import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { RiBookmark3Line } from 'react-icons/ri';
import { useHistory, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import {
  AsyncSelect,
  AsyncSelectOption,
} from '../../../../../../components/Form/AsyncSelect';
import { MaskedInput } from '../../../../../../components/Form/MaskedInput';
import { useAuth } from '../../../../../../hooks/auth';
import { IAppointmentProductBase } from '../../../../../../models/appointments';
import { IProductBase } from '../../../../../../models/products';
import { createAppointmentsService } from '../../../../../../services/Appointments/CreateAppointmentsService';
import {
  IDetailedAppointment,
  showAppointmentsService,
} from '../../../../../../services/Appointments/ShowAppointmentsService';
import { updateAppointmentsService } from '../../../../../../services/Appointments/UpdateAppointmentsService';
import { listUsersService } from '../../../../../../services/Users/ListUsersService';
import { translateError } from '../../../../../../utils/errors';
import {
  maskDateTime,
  maskMoney,
  maskShortDate,
} from '../../../../../../utils/formatters/handleMask';
import { DefaultLayout } from '../../../../_layout/DefaultLayout';
import {
  AppointmentProductTable,
  IAppointmentProductTableItem,
} from '../components/AppointmentProductTable';
import { ProductsAssignModal } from './components/ProductsAssignModal';

interface ILocationState {
  appointmentId?: string;
  productId?: string;
  userId?: string;
}

type HandleAppointmentFormData = {
  userId: string;
  description?: string;
};

const parseAppointmentProductTableItem = (
  appointmentProducts: IAppointmentProductBase[],
): IAppointmentProductTableItem[] =>
  appointmentProducts.map((appointmentProduct) => ({
    ...appointmentProduct,
    formattedCreatedAt: maskDateTime(appointmentProduct.createdAt),
    formattedPrice: maskMoney(appointmentProduct.price),
    formattedStart: appointmentProduct.startAt
      ? maskShortDate(appointmentProduct.startAt)
      : '-',
    formattedFinish: appointmentProduct.finishAt
      ? maskShortDate(appointmentProduct.finishAt)
      : '-',
  }));

const registerAppointmentFormSchema = Yup.object().shape({
  userId: Yup.string()
    .uuid()
    .required('Requerido')
    .transform((value) => value.value),
  description: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
});

export const HandleAppointment = (): JSX.Element => {
  const { goBack, push } = useHistory();
  const toast = useToast();

  const { assignment: authenticatedAssignment } = useAuth();
  const { state } = useLocation<ILocationState | undefined>();

  const appointmentId = state?.appointmentId;
  // const productId = state?.productId;
  const selectedUserId = state?.userId;

  const { control, register, handleSubmit, formState, reset, setValue } =
    useForm({
      resolver: yupResolver(registerAppointmentFormSchema),
    });

  const { errors } = formState;

  const [loading, setLoading] = useState(true);
  const [appointment, setAppointment] = useState<IDetailedAppointment>();
  const [selectedProducts, setSelectedProducts] = useState<
    IAppointmentProductTableItem[]
  >([]);
  const [requiredProductsErrorVisible, setRequiredProductsErrorVisible] =
    useState(false);
  const [isProductsAssignModalVisible, setIsProductsAssignModalVisible] =
    useState(false);

  const currentProductsId = useMemo(
    () => selectedProducts.map((product) => product.productId),
    [selectedProducts],
  );

  const selectedProductsMap = useMemo(
    () =>
      new Map(
        selectedProducts.map((selectedProduct) => [
          selectedProduct.productId,
          selectedProduct,
        ]),
      ),
    [selectedProducts],
  );

  const appointmentTotalValue = useMemo(
    () =>
      maskMoney(
        selectedProducts.reduce(
          (totalValue, product) =>
            totalValue + product.quantity * product.price,
          0,
        ),
      ),
    [selectedProducts],
  );

  useEffect(() => {
    async function loadAppointments(apntId: string): Promise<void> {
      try {
        const appnt = await showAppointmentsService(apntId);

        if (appnt) {
          setAppointment(appnt);

          const parsedProducts = parseAppointmentProductTableItem(
            appnt.products,
          );

          setSelectedProducts(parsedProducts);

          reset({
            userId: { label: appnt.user.name, value: appnt.userId },
            description: appnt.description,
          });
        }
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao carregar os dados do grupo de permissões, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      } finally {
        setLoading(false);
      }
    }

    if (appointmentId) {
      loadAppointments(appointmentId);
    } else {
      setLoading(false);
    }
  }, [appointmentId, reset, toast]);

  const handleUpdateProduct = (product: IAppointmentProductTableItem): void => {
    setSelectedProducts((prevState) =>
      prevState.map((prod) =>
        prod.productId === product.productId ? { ...prod, ...product } : prod,
      ),
    );
  };

  const handleDeleteProduct = (product: IAppointmentProductTableItem): void => {
    setSelectedProducts((prevState) =>
      prevState.filter((prod) => prod.productId !== product.productId),
    );
  };

  const handleToggleProductsAssignModal = useCallback(() => {
    setIsProductsAssignModalVisible((prevState) => !prevState);
  }, []);

  const handleLoadUserSelectOption = useCallback(
    async (name?: string): Promise<AsyncSelectOption[]> => {
      const featureGroups = ['MEMBER', 'GUEST', 'DEPENDANT'];

      if (
        authenticatedAssignment?.featureGroup.features.some((feature) =>
          ['LODGER_FULL_ACCESS', 'LODGER_READ_ACCESS'].includes(feature.key),
        )
      ) {
        featureGroups.push('LODGER');
      }

      const { items: users } = await listUsersService({
        name,
        featureGroups,
        ventureId: authenticatedAssignment?.ventureId,
        limit: 10,
      });

      const parsedUsersSelectOption: AsyncSelectOption[] = [
        ...users.map((user) => ({
          label: user.name,
          value: user.id,
        })),
      ];

      if (selectedUserId) {
        const selectedUser = parsedUsersSelectOption.find(
          (user) => user.value === selectedUserId,
        );

        if (selectedUser) {
          setValue('userId', selectedUser);
        }
      }

      return parsedUsersSelectOption;
    },
    [
      authenticatedAssignment?.featureGroup.features,
      authenticatedAssignment?.ventureId,
      selectedUserId,
      setValue,
    ],
  );

  const handleSelectedProducts = useCallback(
    (assignedProducts: IProductBase[]) => {
      const appointmentProducts = assignedProducts
        .reduce<IAppointmentProductTableItem[]>((response, product) => {
          const existingProduct = selectedProductsMap.get(product.id);

          if (existingProduct) {
            response.push(existingProduct);
          } else {
            const itemCreatedAt = new Date();

            response.push({
              costCenter: product.costCenter,
              description: product.description,
              name: product.name,
              price: 0,
              productCode: product.productCode,
              productId: product.id,
              productNature: product.productNature,
              productType: product.productType,
              invoiceType: product.invoiceType,
              quantity: 1,
              createdAt: itemCreatedAt.toISOString(),
              formattedCreatedAt: maskDateTime(itemCreatedAt),
              formattedPrice: maskMoney(0),
              formattedStart: '-',
              formattedFinish: '-',
            });
          }

          return response;
        }, [])
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );

      setSelectedProducts(appointmentProducts);

      setRequiredProductsErrorVisible(!appointmentProducts.length);

      handleToggleProductsAssignModal();
    },
    [handleToggleProductsAssignModal, selectedProductsMap],
  );

  const handleSubmitAppointment: SubmitHandler<HandleAppointmentFormData> =
    useCallback(
      async ({ userId, description }) => {
        setRequiredProductsErrorVisible(!selectedProducts.length);

        if (!selectedProducts.length) {
          return;
        }

        if (!appointment) {
          try {
            const newAppointment = await createAppointmentsService({
              userId,
              description,
              products: selectedProducts.map((selectedProduct) => ({
                costCenter: selectedProduct.costCenter,
                description: selectedProduct.description,
                finishAt: selectedProduct.finishAt,
                name: selectedProduct.name,
                price: selectedProduct.price,
                productCode: selectedProduct.productCode,
                productId: selectedProduct.productId,
                productNature: selectedProduct.productNature,
                productType: selectedProduct.productType,
                invoiceType: selectedProduct.invoiceType,
                quantity: selectedProduct.quantity,
                startAt: selectedProduct.startAt,
              })),
            });

            toast({
              title: 'Cadastrado com sucesso',
              description: 'A ordem de serviço foi cadastrada corretamente.',
              status: 'success',
              duration: 3000,
              isClosable: true,
              variant: 'subtle',
              position: 'top-right',
            });

            push('/services/appointments/details', {
              appointmentId: newAppointment.id,
            });
          } catch (err) {
            if (axios.isAxiosError(err) && err.response?.status !== 401) {
              toast({
                title: 'Falha no cadastro',
                description:
                  translateError({ message: err.response?.data.message }) ||
                  'Ocorreu um erro ao cadastrar a ordem de serviço, tente novamente.',
                status: 'error',
                duration: 3000,
                isClosable: true,
                variant: 'subtle',
                position: 'top-right',
              });
            }
          }
        } else {
          try {
            const updatedAppointment = await updateAppointmentsService({
              appointmentId: appointment.id,
              userId,
              description,
              products: selectedProducts.map((selectedProduct) => ({
                id: selectedProduct.id,
                costCenter: selectedProduct.costCenter,
                description: selectedProduct.description,
                finishAt: selectedProduct.finishAt,
                name: selectedProduct.name,
                price: selectedProduct.price,
                productCode: selectedProduct.productCode,
                productId: selectedProduct.productId,
                productNature: selectedProduct.productNature,
                productType: selectedProduct.productType,
                invoiceType: selectedProduct.invoiceType,
                quantity: selectedProduct.quantity,
                startAt: selectedProduct.startAt,
              })),
            });

            toast({
              title: 'Atualizado com sucesso',
              description: 'A ordem de serviço foi atualizada corretamente.',
              status: 'success',
              duration: 3000,
              isClosable: true,
              variant: 'subtle',
              position: 'top-right',
            });

            push('/services/appointments/details', {
              appointmentId: updatedAppointment.id,
            });
          } catch (err) {
            if (axios.isAxiosError(err) && err.response?.status !== 401) {
              toast({
                title: 'Falha na atualização',
                description:
                  translateError({ message: err.response?.data.message }) ||
                  'Ocorreu um erro ao atualizar a ordem de serviço, tente novamente.',
                status: 'error',
                duration: 3000,
                isClosable: true,
                variant: 'subtle',
                position: 'top-right',
              });
            }
          }
        }
      },
      [appointment, push, selectedProducts, toast],
    );

  return (
    <DefaultLayout>
      {!loading ? (
        <>
          <ProductsAssignModal
            currentProductsId={currentProductsId}
            isOpen={isProductsAssignModalVisible}
            onClose={handleToggleProductsAssignModal}
            onSubmit={handleSelectedProducts}
          />

          <Box borderRadius={8} flex="1" bg="white" p="8">
            <Box as="form" onSubmit={handleSubmit(handleSubmitAppointment)}>
              <Flex mb="8" justify="space-between" align="center">
                {!appointment ? (
                  <Heading size="lg" fontWeight="normal">
                    Cadastrar ordem de serviço
                  </Heading>
                ) : (
                  <Heading size="lg" fontWeight="normal">
                    Editar ordem de serviço
                  </Heading>
                )}

                <ButtonGroup>
                  <Button size="sm" colorScheme="blackAlpha" onClick={goBack}>
                    Cancelar
                  </Button>

                  <Button
                    size="sm"
                    type="submit"
                    colorScheme="green"
                    isLoading={formState.isSubmitting}
                  >
                    Salvar
                  </Button>
                </ButtonGroup>
              </Flex>

              <Divider my="6" borderColor="gray.300" />

              <VStack spacing="8">
                <AsyncSelect
                  label="Usuário"
                  name="userId"
                  loadOptions={handleLoadUserSelectOption}
                  control={control}
                  error={errors.userId}
                />

                <MaskedInput
                  label="Descrição"
                  as="textarea"
                  minHeight="160px"
                  resize="none"
                  py="2"
                  error={errors.description}
                  {...register('description')}
                />
              </VStack>
            </Box>

            <Box mt="4">
              <Flex justify="space-between" align="center">
                <Heading size="md" fontWeight="normal">
                  Produtos
                </Heading>

                <Button
                  size="sm"
                  fontSize="sm"
                  colorScheme="green"
                  onClick={handleToggleProductsAssignModal}
                  leftIcon={<Icon as={RiBookmark3Line} fontSize="20" />}
                >
                  Vincular produtos
                </Button>
              </Flex>

              <HStack
                mt="4"
                px="2"
                justifyContent={
                  requiredProductsErrorVisible ? 'space-between' : 'flex-end'
                }
              >
                {!!requiredProductsErrorVisible && (
                  <Text fontSize="sm" color="red.500">
                    * Requerido no mínimo 1 produto
                  </Text>
                )}

                <HStack fontSize="lg">
                  <Text>Valor total:</Text>
                  <Text>{appointmentTotalValue}</Text>
                </HStack>
              </HStack>

              <AppointmentProductTable
                appointmentProducts={selectedProducts}
                disableProductDetailsButton
                onDelete={handleDeleteProduct}
                onUpdate={handleUpdateProduct}
              />
            </Box>
          </Box>
        </>
      ) : (
        <></>
      )}
    </DefaultLayout>
  );
};
